import React, { useState } from "react";
import { Button, Text, Flex, Divider } from "@fluentui/react-northstar";

/**
 * DangerZoneCard - Card for dangerous operations with confirmation dialogs.
 *
 * Props:
 *   onRemoveAllMappings (async fn) - handler for removing all mappings
 *   onResetEverything (async fn) - handler for full reset
 *   loading (bool) - external loading state
 */
const DangerZoneCard = ({ onRemoveAllMappings, onResetEverything, loading }) => {
    const [confirmDialog, setConfirmDialog] = useState(null); // 'remove' | 'reset' | null
    const [actionLoading, setActionLoading] = useState(false);

    const handleConfirm = async () => {
        setActionLoading(true);
        try {
            if (confirmDialog === 'remove' && onRemoveAllMappings) {
                await onRemoveAllMappings();
            } else if (confirmDialog === 'reset' && onResetEverything) {
                await onResetEverything();
            }
            setActionLoading(false);
            setConfirmDialog(null);
        } catch (e) {
            setActionLoading(false);
        }
    };

    const dialogConfig = {
        remove: {
            title: 'Alle Zuordnungen entfernen',
            message: 'Alle Mitarbeiter-Zuordnungen werden entfernt.',
            confirmLabel: 'Entfernen',
        },
        reset: {
            title: 'Alles zurücksetzen',
            message: 'Alle Zuordnungen, Konfigurationen und Abonnements werden gelöscht. Dies kann nicht rückgängig gemacht werden.',
            confirmLabel: 'Zurücksetzen',
        },
    };

    const currentDialog = confirmDialog ? dialogConfig[confirmDialog] : null;

    return (
        <>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '6px',
                borderLeft: '3px solid #a4262c',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                padding: '20px 24px',
            }}>
                <Text as="h3" style={{ color: '#a4262c', fontWeight: 600, fontSize: '16px', margin: '0 0 16px 0' }}>
                    Gefahrenzone
                </Text>

                <Flex column gap="gap.medium">
                    <Flex column gap="gap.smaller">
                        <Button
                            content="Alle Zuordnungen entfernen"
                            disabled={loading || actionLoading}
                            style={{
                                border: '1px solid #a4262c',
                                color: '#a4262c',
                                backgroundColor: 'transparent',
                                alignSelf: 'flex-start',
                            }}
                            onClick={() => setConfirmDialog('remove')}
                        />
                        <Text size="small" style={{ color: '#605e5c' }}>
                            Entfernt alle Mitarbeiter-Zuordnungen zwischen Teams-Benutzern und ebuero-Kontakten.
                        </Text>
                    </Flex>

                    <Flex column gap="gap.smaller">
                        <Button
                            content="Alles zurücksetzen"
                            disabled={loading || actionLoading}
                            style={{
                                backgroundColor: '#a4262c',
                                color: 'white',
                                alignSelf: 'flex-start',
                            }}
                            onClick={() => setConfirmDialog('reset')}
                        />
                        <Text size="small" style={{ color: '#605e5c' }}>
                            Setzt alle Zuordnungen, Konfigurationen und Abonnements zurück. Diese Aktion kann nicht rückgängig gemacht werden.
                        </Text>
                    </Flex>
                </Flex>
            </div>

            {/* Confirmation dialog overlay */}
            {currentDialog && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 100000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        backgroundColor: '#fff', padding: '24px', borderRadius: '8px',
                        minWidth: '400px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                        borderTop: '3px solid #a4262c',
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <Flex column gap="gap.medium">
                            <Flex hAlign="center">
                                <Text content={currentDialog.title} size="large" weight="bold" />
                            </Flex>
                            <Divider />
                            <Text content={currentDialog.message} />
                            <Flex hAlign="end" gap="gap.small" style={{ marginTop: '20px' }}>
                                <Button
                                    content="Abbrechen"
                                    disabled={actionLoading}
                                    onClick={() => setConfirmDialog(null)}
                                />
                                <Button
                                    content={currentDialog.confirmLabel}
                                    loading={actionLoading}
                                    disabled={actionLoading}
                                    primary
                                    style={{ backgroundColor: '#a4262c', color: 'white' }}
                                    onClick={handleConfirm}
                                />
                            </Flex>
                        </Flex>
                    </div>
                </div>
            )}
        </>
    );
};

export default DangerZoneCard;
